import React, { useEffect, useMemo, useState } from "react";
import { Search, Plus, Tag } from "lucide-react";
import PageHeader from "../../components/ui/PageHeader";
import { ConfirmModal } from "../../components/ui/ConfirmModal";
import { DealsTable } from "../../components/deals/DealsTable";
import {
  DealForm,
  DealFormSavePayload,
} from "../../components/deals/DealForm";
import { useToast } from "../../hooks/useToast";
import { getBusinessServices } from "../../services/businessServicesApi";
import { getAdminProducts } from "../../services/productsApi";
import { BusinessService } from "../../types/businessService";
import { Product } from "../../types/product";
import {
  createManageDeal,
  deleteManageDeal,
  getManageDeal,
  getManageDeals,
  ManageDeal,
  updateManageDeal,
} from "../../services/manageDealsApi";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const DealsPage: React.FC = () => {
  const toast = useToast();

  const [rows, setRows] = useState<ManageDeal[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState<"create" | "edit">("create");
  const [editingDeal, setEditingDeal] = useState<ManageDeal | null>(null);
  const [saving, setSaving] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  const [catalogLoading, setCatalogLoading] = useState(false);
  const [services, setServices] = useState<BusinessService[]>([]);
  const [products, setProducts] = useState<Product[]>([]);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedDelete, setSelectedDelete] = useState<ManageDeal | null>(null);

  const fetchDeals = async () => {
    try {
      setLoading(true);
      const res = await getManageDeals({
        per_page: 200,
      });
      setRows(res.data || []);
    } catch (e: any) {
      toast.error(
        e?.response?.data?.message || e?.message || "Failed to load deals.",
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchDeals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadCatalog = async () => {
    if (services.length && products.length) return;
    setCatalogLoading(true);
    try {
      const [svc, prod] = await Promise.all([
        getBusinessServices(),
        getAdminProducts(),
      ]);
      setServices(svc.filter((s) => s.status === "active"));
      setProducts(prod);
    } catch {
      toast.error("Failed to load services/products.");
    } finally {
      setCatalogLoading(false);
    }
  };

  const openCreate = async () => {
    setFormMode("create");
    setEditingDeal(null);
    setShowForm(true);
    await loadCatalog();
  };

  const openEdit = async (deal: ManageDeal) => {
    setFormMode("edit");
    setEditingDeal(deal);
    setShowForm(true);
    setFormLoading(true);
    await loadCatalog();
    try {
      const full = await getManageDeal(deal.id);
      setEditingDeal(full);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to load deal.");
      setShowForm(false);
      setEditingDeal(null);
    } finally {
      setFormLoading(false);
    }
  };

  const handleCancelForm = () => {
    if (saving) return;
    setShowForm(false);
    setEditingDeal(null);
  };

  const handleSave = async (payload: DealFormSavePayload) => {
    if (!payload.title.trim()) {
      toast.error("Deal title is required.");
      return;
    }
    if (payload.items.length === 0) {
      toast.error("Add at least one service or product.");
      return;
    }

    setSaving(true);
    try {
      if (formMode === "edit" && editingDeal?.id) {
        await updateManageDeal(editingDeal.id, payload);
        toast.success("Deal updated.");
      } else {
        await createManageDeal(payload);
        toast.success("Deal created.");
      }
      setShowForm(false);
      setEditingDeal(null);
      await fetchDeals();
    } catch (e: any) {
      toast.error(
        e?.response?.data?.message || e?.message || "Failed to save deal.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedDelete) return;
    try {
      await deleteManageDeal(selectedDelete.id);
      toast.success("Deal deleted.");
      setDeleteOpen(false);
      setSelectedDelete(null);
      await fetchDeals();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to delete deal.");
    }
  };

  const filteredRows = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return rows.filter((r) => {
      const matchesSearch = !q || r.title.toLowerCase().includes(q);
      const matchesStatus =
        statusFilter === "all" ||
        (r.status || "").toLowerCase() === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [rows, searchTerm, statusFilter]);

  const paginatedRows = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredRows.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredRows, currentPage, itemsPerPage]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / itemsPerPage));

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, itemsPerPage]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-4 lg:p-6">
        <div className="flex flex-col w-full h-full">
          <PageHeader
            title="Deals"
            description="Bundle services and products, then sell them in POS Quick Sale"
            icon={Tag}
            variant="card"
            className="mb-0"
            mobileActionsInHeader={true}
            actions={
              <button
                type="button"
                onClick={() => void openCreate()}
                className="px-3 py-1.5 lg:px-6 lg:py-3 bg-[#885ABB] hover:bg-[#6f47a2] text-white rounded-lg text-[10px] lg:text-base font-bold lg:font-medium transition-all transform hover:-translate-y-0.5 flex items-center gap-1.5 lg:gap-2 shadow-sm"
              >
                <Plus className="w-3.5 h-3.5 lg:w-5 lg:h-5" />
                <span>Add Deal</span>
              </button>
            }
          />

          <div className="flex flex-col lg:flex-row lg:items-center justify-between py-4 gap-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500 font-medium whitespace-nowrap">
                Show
              </span>
              <Select
                value={itemsPerPage.toString()}
                onValueChange={(value) => {
                  setItemsPerPage(Number(value));
                  setCurrentPage(1);
                }}
              >
                <SelectTrigger className="w-[80px] bg-white border-gray-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-gray-500 font-medium whitespace-nowrap">
                per page
              </span>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full lg:w-auto">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-[140px] bg-white border-gray-200">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>

              <div className="relative w-full lg:w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search deals..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg pl-10 pr-4 py-2 text-[14px] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#885ABB] focus:border-transparent transition-all"
                />
              </div>
            </div>
          </div>

          <div className="flex-1 pb-6">
            <DealsTable
              deals={paginatedRows}
              onEdit={(deal) => void openEdit(deal)}
              onDelete={(deal) => {
                setSelectedDelete(deal);
                setDeleteOpen(true);
              }}
              isLoading={loading}
            />

            {!loading && totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-4 mt-4 border-t border-gray-200 bg-white rounded-b-xl">
                <div className="text-sm text-gray-600 text-center sm:text-left">
                  Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                  {Math.min(currentPage * itemsPerPage, filteredRows.length)} of{" "}
                  {filteredRows.length} deals
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(1, prev - 1))
                    }
                    disabled={currentPage === 1}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-600 whitespace-nowrap">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                    }
                    disabled={currentPage === totalPages}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {showForm && (
          <DealForm
            mode={formMode}
            deal={editingDeal}
            services={services}
            products={products}
            catalogLoading={catalogLoading}
            formLoading={formLoading}
            saving={saving}
            onSave={handleSave}
            onCancel={handleCancelForm}
          />
        )}
      </div>

      <ConfirmModal
        isOpen={deleteOpen}
        onClose={() => {
          setDeleteOpen(false);
          setSelectedDelete(null);
        }}
        onConfirm={handleDelete}
        title="Delete deal?"
        message={`Delete “${selectedDelete?.title || "this deal"}”? This cannot be undone.`}
        confirmText="Delete"
      />
    </div>
  );
};

export default DealsPage;
